import React, { useState } from "react";

import EditIcon from "@mui/icons-material/Edit";
import { Alert, Box, Button, CardContent, CardHeader, CircularProgress, TextField, Typography } from "@mui/material";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import { apiClient } from "../api/client";
import { ProgramDateExtended, SetRestrictionsRequest } from "../api/PartnerPortalGatewayApi";
import { MainLayout } from "../components/layout/MainLayout";
import { CACard, CADialog, UniversalDataGrid } from "../components/ui";
import { useLoading } from "../contexts/LoadingContext";
import { useNotification } from "../contexts/NotificationContext";
import { useAppSelector } from "../store/hooks";

const RocketeersDays: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const { showLoading, hideLoading } = useLoading();
  const queryClient = useQueryClient();
  const selectedService = useAppSelector((state) => state.service.selectedService);
  const user = useAppSelector((state) => state.auth.user);

  // State for restrictions dialog
  const [restrictionDialogOpen, setRestrictionDialogOpen] = useState(false);
  const [selectedProgramDateId, setSelectedProgramDateId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [restrictions, setRestrictions] = useState<string>("");

  const currentYear = new Date().getFullYear();
  const selectedServiceId = selectedService?.serviceId;

  // Fetch program dates
  const {
    data: programDates,
    isLoading: isLoadingDates,
    error: datesError,
  } = useQuery({
    queryKey: ["holidayClubProgramDates", selectedServiceId, currentYear],
    queryFn: () => apiClient.holidayClub.getProgramDates(selectedServiceId!, currentYear),
    enabled: !!selectedServiceId,
  });

  // Mutations
  const setRestrictionsMutation = useMutation({
    mutationFn: ({ programDateId, request }: { programDateId: number; request: SetRestrictionsRequest }) =>
      apiClient.holidayClub.setProgramDateRestrictions(programDateId, request),
    onMutate: () => showLoading("Saving restrictions..."),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["holidayClubProgramDates"] });
      setRestrictionDialogOpen(false);
      setRestrictions("");
      setSelectedProgramDateId(null);
      setSelectedDate("");
      showSuccess("Rocketeers Date restrictions saved");
      hideLoading();
    },
    onError: () => {
      showError("Save Rocketeers Date restrictions failed");
      hideLoading();
    },
  });

  const handleOpenRestrictionsDialog = (programDateId: number, date: string, currentRestrictions: string) => {
    setSelectedProgramDateId(programDateId);
    setSelectedDate(date);
    setRestrictions(currentRestrictions || "");
    setRestrictionDialogOpen(true);
  };

  const handleSaveRestrictions = () => {
    if (!selectedProgramDateId || !user?.id) return;

    setRestrictionsMutation.mutate({
      programDateId: selectedProgramDateId,
      request: new SetRestrictionsRequest({
        restrictions,
      }),
    });
  };

  // Program Dates Grid
  const programDatesColumns: GridColDef[] = [
    {
      field: "date",
      headerName: "Date",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => dayjs(params.row.date).format("DD/MM/YYYY"),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "programActivityTypeId",
      headerName: "Type",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => params.row.activityTypeName,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() =>
              handleOpenRestrictionsDialog(
                params.row.programDateId,
                dayjs(params.row.date).format("DD/MM/YYYY"),
                params.row.restrictions,
              )
            }
          >
            Restrictions
          </Button>
          {/* {(!params.row.isConfirmed || !params.row.isConfirmed) && (
            <Button
              size="small"
              startIcon={<CheckIcon />}
              onClick={() => handleConfirmDate(params.row.programDateId)}
              disabled={confirmDateMutation.isPending}
            >
              Confirm
            </Button>
          )}
          {params.row.isConfirmed && (
            <Typography variant="body2" color="success.main" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CheckCircleIcon fontSize="small" />
              Confirmed
            </Typography>
          )} */}
        </Box>
      ),
    },
  ];

  const programDatesRows: GridRowsProp =
    programDates?.map((date: ProgramDateExtended, index: number) => ({
      id: date.programDateId,
      rowNumber: index + 1,
      ...date,
    })) || [];

  if (isLoadingDates) {
    return (
      <MainLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (datesError) {
    return (
      <MainLayout>
        <Alert severity="error">Error loading Holiday Club data: {(datesError as Error)?.message}</Alert>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Rocketeers Dates Section */}
        <CACard>
          <CardHeader title="Rocketeers Dates" action={null} />
          <CardContent>
            <UniversalDataGrid
              rows={programDatesRows}
              columns={programDatesColumns}
              loading={isLoadingDates}
              pageSize={10}
            />
          </CardContent>
        </CACard>
      </Box>

      {/* Restrictions Dialog */}
      <CADialog
        open={restrictionDialogOpen}
        onClose={() => setRestrictionDialogOpen(false)}
        title="Edit Restrictions"
        maxWidth="sm"
        onConfirm={handleSaveRestrictions}
        confirmText="Save"
        isLoading={setRestrictionsMutation.isPending}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          Restrictions on <strong>{selectedDate}</strong>
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={restrictions}
          onChange={(e) => setRestrictions(e.target.value)}
          placeholder="Enter restrictions..."
          inputProps={{ maxLength: 255 }}
        />
      </CADialog>
    </MainLayout>
  );
};

export default RocketeersDays;
